import { api } from "../api";
import type { PortalFeatureInfo, SchoolSettings } from "../types";
import { esc } from "../utils/html";

/**
 * Einstellungen → Schüler-Portal: Menüpunkte und Unterfunktionen des Portals
 * pro Schule ein- und ausschalten. Der Katalog kommt aus dem Backend
 * (backend/src/services/student-portal-features.ts), damit beide Seiten
 * dieselbe Liste kennen.
 */

function showToast(message: string) {
  const toast = document.createElement("div");
  toast.className = "toast";
  toast.textContent = message;
  document.body.appendChild(toast);
  requestAnimationFrame(() => toast.classList.add("toast--visible"));
  setTimeout(() => {
    toast.classList.remove("toast--visible");
    setTimeout(() => toast.remove(), 300);
  }, 2500);
}

/** Anzeigenamen der kaufbaren Module, an denen Portal-Funktionen hängen. */
const MODULE_LABELS: Record<string, string> = {
  planung: "Praktikumsplanung",
  stundenplan: "Stundenplan",
};

/**
 * Eine Funktion, deren Modul fehlt, wird gesperrt und ohne Haken gezeigt: Ein Schalter,
 * der nachweislich wirkungslos ist, wäre irreführend. Der Hinweis steht außerhalb des
 * Labels, damit der Link zu den Abonnements anklickbar bleibt.
 */
function renderToggle(
  f: PortalFeatureInfo,
  enabled: boolean,
  isChild: boolean,
  blockedNote?: string
): string {
  const locked = !!blockedNote;
  const size = isChild ? "14px" : "16px";
  return `
    <div>
      <label style="display:flex;align-items:flex-start;gap:.75rem;cursor:${locked ? "default" : "pointer"}${locked ? ";opacity:.55" : ""}">
        <input type="checkbox" class="portal-feature-toggle" data-key="${esc(f.key)}"
          ${locked ? "disabled" : enabled ? "checked" : ""}
          style="width:${size};height:${size};margin-top:2px;accent-color:var(--primary);flex-shrink:0" />
        <div>
          <div style="font-weight:500;font-size:${isChild ? ".8125rem" : ".875rem"}">${esc(f.label)}</div>
          <div style="font-size:.8125rem;color:var(--gray-500);margin-top:.25rem">${esc(f.description)}</div>
        </div>
      </label>
      ${locked ? `
      <div style="font-size:.8125rem;color:var(--gray-500);margin:.35rem 0 0 ${isChild ? "1.85rem" : "2.25rem"}">
        ${esc(blockedNote)} <a href="#/abonnements">Zu den Abonnements</a>
      </div>` : ""}
    </div>`;
}

const PAGE_HEADER = `
  <div class="settings-page-header">
    <div>
      <h1>Schüler-Portal</h1>
      <p>Welche Funktionen deine Schüler in ihrem Portal sehen.</p>
    </div>
  </div>`;

export async function render(el: HTMLElement) {
  let settings: SchoolSettings;
  try {
    settings = await api.settings.get();
  } catch (err: any) {
    el.innerHTML = `${PAGE_HEADER}<div class="alert alert-error">Fehler beim Laden: ${esc(err.message)}</div>`;
    return;
  }

  if (!settings.schuelerPortalEnabled) {
    el.innerHTML = `
      ${PAGE_HEADER}
      <div class="card" style="padding:1.5rem">
        <p style="font-size:.875rem;color:var(--gray-600);margin:0 0 1rem">
          Das Schüler-Portal ist für deine Schule nicht aktiviert. Sobald es freigeschaltet ist,
          kannst du hier einzelne Funktionen für deine Schüler ein- und ausblenden.
        </p>
        <a class="btn btn-primary" href="#/abonnements">Zu den Abonnements</a>
      </div>`;
    return;
  }

  const catalog = settings.studentPortalFeatureCatalog ?? [];
  const state: Record<string, boolean> = { ...(settings.studentPortalFeatures ?? {}) };
  const parents = catalog.filter(f => !f.parent);

  const moduleOn: Record<string, boolean> = {
    planung: !!settings.planungEnabled,
    stundenplan: !!settings.stundenplanEnabled,
  };
  /** Name des fehlenden Moduls, sonst null. */
  const blockedBy = (f: PortalFeatureInfo): string | null =>
    f.requiresModule && !moduleOn[f.requiresModule] ? f.requiresModule : null;

  /** Hinweis unter einer gesperrten Funktion, inklusive überlebender Unterfunktionen. */
  const blockedNote = (module: string, survivors: PortalFeatureInfo[]): string => {
    const base = `Benötigt das Modul „${MODULE_LABELS[module] ?? module}".`;
    if (survivors.length === 0) return base;
    const names = survivors.map(s => `„${s.label}"`).join(" und ");
    return `${base} ${names} ${survivors.length === 1 ? "bleibt" : "bleiben"} davon unberührt.`;
  };

  const renderBody = () => {
    el.innerHTML = `
      ${PAGE_HEADER}
      <div class="card" style="padding:1.5rem">
        <h2 style="font-size:1rem;font-weight:600;margin-bottom:.75rem">Funktionen im Schüler-Portal</h2>
        <div style="font-size:.8125rem;color:var(--gray-500);margin-bottom:1.25rem">
          Abgeschaltete Funktionen verschwinden aus dem Menü deiner Schüler und sind auch über
          einen direkten Link nicht mehr erreichbar. "Mein Dashboard" bleibt immer verfügbar.
          Funktionen, für die das passende Modul fehlt, sind gesperrt. Ihre Einstellung bleibt
          gespeichert und gilt wieder, sobald das Modul gebucht ist.
        </div>
        <div style="display:flex;flex-direction:column;gap:1rem">
          ${parents.map(parent => {
            const children = catalog.filter(f => f.parent === parent.key);
            const parentBlocked = blockedBy(parent);
            const parentOn = !parentBlocked && state[parent.key] !== false;
            // Unterfunktionen bleiben sichtbar, wenn der Menüpunkt nur am fehlenden Modul
            // hängt: Sie können ohne dieses Modul weiterlaufen, etwa die Wunschliste.
            const showChildren = children.length > 0 && (parentOn || !!parentBlocked);
            const survivors = parentBlocked ? children.filter(c => !blockedBy(c)) : [];
            return `<div>
              ${renderToggle(parent, parentOn, false,
                parentBlocked ? blockedNote(parentBlocked, survivors) : undefined)}
              ${showChildren ? `
              <div style="margin:.5rem 0 0 2.25rem;padding:.75rem;background:var(--gray-50);border-radius:.5rem;display:flex;flex-direction:column;gap:.5rem">
                ${children.map(child => {
                  const childBlocked = blockedBy(child);
                  return renderToggle(child, !childBlocked && state[child.key] !== false, true,
                    childBlocked ? blockedNote(childBlocked, []) : undefined);
                }).join("")}
              </div>` : ""}
            </div>`;
          }).join("")}
        </div>
      </div>`;

    el.querySelectorAll<HTMLInputElement>(".portal-feature-toggle").forEach(cb => {
      cb.addEventListener("change", async () => {
        const key = cb.dataset.key!;
        const enabled = cb.checked;
        cb.disabled = true;
        try {
          const result = await api.settings.setStudentPortalFeature(key, enabled);
          Object.assign(state, result.features);
          const label = catalog.find(f => f.key === key)?.label ?? key;
          showToast(`"${label}" ${enabled ? "aktiviert" : "deaktiviert"}`);
          // Neu rendern, damit Unterfunktionen dem Menüpunkt folgen
          renderBody();
        } catch (err: any) {
          cb.checked = !enabled;
          cb.disabled = false;
          showToast("Fehler: " + err.message);
        }
      });
    });
  };

  renderBody();
}
